import React from 'react';
import PropTypes from 'prop-types';
import {createSelector} from 'reselect';

import {isRequiredIf} from '@stubhub/custom-proptypes';
import {connect} from '@stubhub/react-store-provider';

import {hostSelector} from './selector';
const EVENT_CATEGORY_MAP = {
  1: 'MusicEvent', // Concert
  28: 'SportsEvent',
  174: 'TheaterEvent',
  209: 'ComedyEvent',
  7168: 'TheaterEvent',
  10949: 'SportsEvent',
  23390: 'Festival',
};

const PERFORMER_CATEGORY_MAP = {
  1: 'MusicGroup', // Concert
  28: 'SportsTeam',
  174: 'TheaterGroup',
  209: 'Person',
  7168: 'TheaterGroup',
  10949: 'Person',
  23390: 'FestivalGroup',
};

const EVENT_NAME_MAX_LENGTH = 80;
const EVENT_SATE_SLICE_END = -6;

const eventSelector = (state) => state.event;
const eventDetailSelector = createSelector(eventSelector, (event = {}) => {
  return event;
});

const mapStateToProps = (state) => {
  return {
    ...eventDetailSelector(state),
    host: hostSelector(state),
  };
};

export class GoogleEvent extends React.Component {
  render() {
    const {eventAttributes} = this.props;
    if (!isDisabled(eventAttributes, {Parking: 1})) {
      const scriptData = parse(this.props);

      /* eslint-disable react/no-danger */
      return (
        <script
          id="google_events_tag"
          type="application/ld+json"
          dangerouslySetInnerHTML={{__html: JSON.stringify(scriptData)}}
        />
      );
      /* eslint-enable react/no-danger */
    }

    return null;
  }
}
GoogleEvent.propTypes = {
  eventAttributes: isRequiredIf(PropTypes.object, (props) => props.loading === false),
};

const ConnectedGoogleEvent = connect({mapStateToProps})(GoogleEvent);
export default ConnectedGoogleEvent;

export function withGoogleEvent(InnerComponent) {
  // eslint-disable-next-line react/display-name
  return (props = {}) => (
    <>
      <ConnectedGoogleEvent />
      <InnerComponent {...props} />
    </>
  );
}

/**
 *
 * @param categoriesArray
 * @description return category text based on category ids by passing in a prioritized array of categories lowest priority to highest priority
 * @example _getEventCategory([{name: 'least important', id: 0}, {name: 'high priority', id:174}])
 * @returns String
 * @private
 */
function _getEventCategory(categoriesArray) {
  const category = _getCategory(categoriesArray, EVENT_CATEGORY_MAP);
  if (category) {
    return category;
  }

  // If no category found, return generic 'Event' response
  return 'Event';
}

/**
 *
 * @param categoriesArray
 * @description return category text based on category ids by passing in a prioritized array of categories lowest priority to highest priority
 * @example _getEventCategory([{name: 'least important', id: 0}, {name: 'high priority', id:174}])
 * @returns String
 * @private
 */
function _getPerformerCategory(categoriesArray) {
  const category = _getCategory(categoriesArray, PERFORMER_CATEGORY_MAP);
  if (category) {
    return category;
  }

  // If no category found, return generic 'Performer' response
  return 'Performer';
}

function _getLink(host, urlPath) {
  return `https://${host}/${urlPath}`;
}

function isDisabled(eventAttributes = {}, eventTypeBlacklist) {
  const {eventType} = eventAttributes;

  return eventType && eventTypeBlacklist[eventType];
}

function parseEvent(data) {
  const {webURI, eventDateLocal, name, status, images = [], ancestors = {}, seoMeta = {}, host, dateLastChance} = data;
  // Validate requirement to prevent throwing errors

  if (seoMeta && ancestors) {
    return {
      type: _getEventCategory(ancestors.categories),
      name: name.substr(0, EVENT_NAME_MAX_LENGTH),
      description: seoMeta.metaDescription,
      image: images[0].url,
      url: _getLink(host, webURI),
      startDate: eventDateLocal.slice(0, EVENT_SATE_SLICE_END),
      status,
      dateLastChance,
    };
  }
}

function parsePerformers({performers = [], ancestors = {}, host}) {
  // Validate requirement to prevent throwing errors
  if (performers.length && ancestors) {
    const performersArrayLength = performers.length;
    const performersArray = [];

    for (let i = 0; i < performersArrayLength; i++) {
      performersArray.push({
        type: _getPerformerCategory(ancestors.categories),
        name: performers[i].name,
        url: _getLink(host, performers[i].webURI),
      });
    }

    if (performersArrayLength <= 0) {
      return null;
    }

    return performersArray;
  }
}

function parseLocation(venue) {
  // Validate requirement to prevent throwing errors
  if (venue) {
    const {name, address1, locality, state, postalCode, country} = venue;

    return {
      type: 'Place',
      name,
      address: {
        type: 'PostalAddress', // TODO: identify this field (Events-2224 comments change to PostalAddress)
        streetAddress: address1,
        locality,
        region: state,
        postalCode,
        country,
      },
      primaryTicketerLink: 'unavailable', // TODO: missing primary link
    };
  }
}

function parseOffers(events) {
  if (events && events.ticketInfo) {
    const {createdDate} = events;
    const {minPrice, maxPrice, currencyCode, totalTickets} = events.ticketInfo;

    return {
      minTicketPrice: minPrice,
      maxTicketPrice: maxPrice,
      minTicketPriceWithCurrency: currencyCode,
      totalTickets,
      createdDate,
    };
  }

  return {
    minTicketPrice: '',
    maxTicketPrice: '',
    minTicketPriceWithCurrency: '',
    totalTickets: '',
    createdDate: '',
  };
}

function parse(data = {}) {
  // Event section required only event response
  if (data.id) {
    const event = parseEvent(data);
    const location = parseLocation(data.venue);
    const performers = parsePerformers(data) || [];
    const ticketInfo = parseOffers(data.eventStructureData);
    const {name, type, description, image, url, startDate, status, dateLastChance} = event;

    const eventDt = new Date(startDate);
    const eventEndDate = `${eventDt.getFullYear()}-${eventDt.getMonth() + 1}-${eventDt.getDate()}`;

    const eventStatusMapping = {
      active: 'https://schema.org/EventScheduled',
      completed: 'https://schema.org/EventScheduled',
      scheduled: 'https://schema.org/EventScheduled',
      contingent: 'https://schema.org/EventScheduled',
      postponed: 'https://schema.org/EventPostponed',
      inactive: 'https://schema.org/EventPostponed',
      cancelled: 'https://schema.org/EventCancelled',
    };

    const response = {
      '@context': 'https://schema.org',
      '@type': type,
      name,
      description,
      image,
      url,
      startDate,
      endDate: eventEndDate,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: eventStatusMapping[status] || 'https://schema.org/EventScheduled',
      location: {
        '@type': location.type,
        name: location.name,
        address: {
          '@type': location.address.type,
          streetAddress: location.address.streetAddress,
          addressLocality: location.address.locality,
          addressRegion: location.address.region,
          postalCode: location.address.postalCode,
          addressCountry: location.address.country,
        },
        sameAs: 'unavailable',
      },
      offers: {
        '@type': 'AggregateOffer',
        name: 'Resale',
        category: 'Secondary',
        url,
        availability: 'https://schema.org/InStock',
        lowPrice: ticketInfo.minTicketPrice,
        highPrice: ticketInfo.maxTicketPrice,
        priceCurrency: ticketInfo.minTicketPriceWithCurrency,
        validFrom: ticketInfo.createdDate,
        validThrough: dateLastChance,
        inventoryLevel: {
          '@type': 'QuantitativeValue',
          name: ticketInfo.totalTickets,
        },
      },
    };

    if (performers.length) {
      response.performers = performers.map((performer) => {
        return {
          '@type': performer.type,
          name: performer.name,
          sameAs: performer.url,
        };
      });
    }

    return response;
  }
}

/**
 * @param map : Object containing category map
 * @param categoriesArray
 * @description return category text based on category ids by passing in a prioritized array of categories lowest priority to highest priority
 * @example _getCategory([{name: 'least important', id: 0}, {name: 'high priority', id:174}])
 * @returns String
 * @private
 */
function _getCategory(categoriesArray = [], map) {
  const categoryArrayLength = categoriesArray.length;
  let category;

  for (let i = categoryArrayLength - 1; i >= 0; i--) {
    category = map[categoriesArray[i].id];

    if (category) {
      return category;
    }
  }

  return category;
}

// TODO: Handle offers

/*
 * ParseOffers: function() {
 *     // validate requirement to prevent throwing errors
 *     if (this.eventResponse &&
 *         this.eventResponse.venue &&
 *         this.inventoryResponse &&
 *         this.inventoryResponse.pricingSummary) {
 */

/*
 *         Var pricing = this.inventoryResponse.pricingSummary,
 *             offers = [
 *                 {
 *                     type: 'AggregateOffer',
 *                     name: 'Resale',
 *                     url: this._getLink(this.eventResponse.webURI),
 *                     category: 'Secondary',
 *                     availability: 'https://schema.org/InStock',
 *                     lowPrice: pricing.minTicketPrice,
 *                     highPrice: pricing.maxTicketPrice,
 *                     priceCurrency: pricing.minTicketPriceWithCurrency.currency,
 *                     inventoryLevel: this.inventoryResponse.totalTickets,
 *                     valid: {
 *                         fromDate: this.eventResponse.onSaleDate,
 *                         toDate: this.eventResponse.dateLastChance
 *                     }
 *                 }
 *             ];
 */

//         This.set('offers', offers);

/*
 *         Return offers;
 *     }
 * },
 */

/*
 * TODO: Handle offers.
 *     {@if test="offers.length > 0"}
 *     "offers": [
 *     {#offers}
 *     {
 *         "@type": "{type}",
 *         "name": "{name}",
 *         "url": "{url}",
 *         "category": "{category}",
 *         "availability": "{availability}",
 *         "lowPrice": "{lowPrice}",
 *         "highPrice": "{highPrice}",
 *         "priceCurrency": "{priceCurrency}",
 *         "inventoryLevel": "{inventoryLevel}",
 *         "validFrom": "{valid.fromDate}",
 *         "validThrough": "{valid.toDate}"
 *     }{@sep},{/sep}
 *     {/offers}
 *     ],
 * {/if}
 */
